import { Badge, Button, Flex, Heading, Text, useToast } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import {
  organizationListContext,
  userListContext,
} from 'BrokerTsLib/src/context'
import {
  FlexGroup,
  OrganizationUnitGridItem,
  UserGridItem,
} from 'BrokerTsLib/src/dtos'
import {
  useAddFlexGroupMember,
  useAddFlexGroupUser,
  useGetById,
  useRemoveFlexGroupMember,
  useRemoveFlexGroupUser,
} from 'BrokerTsLib/src/hooks/useFlexGroup'
import { noop } from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Container } from '@/components/Container'
import { Loading } from '@/components/Loading'
import { Section } from '@/components/Section'
import { AddFlexGroupMember } from '@/containers/flex-group/AddFlexGroupMember'
import { AddFlexGroupUser } from '@/containers/flex-group/AddFlexGroupUser'
import {
  OrganizationListCityColumn,
  OrganizationListContactPersonColumn,
  OrganizationListDescriptionColumn,
  OrganizationListNameColumn,
  OrganizationListTypeColumn,
  OrganizationsList,
} from '@/containers/organization/OrganizationList'
import { PageContent } from '@/containers/PageContent'
import { PageToolbar } from '@/containers/PageToolbar'
import { ListItemRemovalButton } from '@/containers/shared/ListItemActionButtons'
import { SectionHeading } from '@/containers/shared/SectionHeading'
import { ValidityBadge } from '@/containers/shared/ValidityBadge'
import { UserList, UserListDefaultColumns } from '@/containers/users/UserList'
import { ArrowLeft, Edit, TextUnorderedList } from '@/icons'

export const FlexGroupDetailPage = () => {
  const navigate = useNavigate()
  const { flexGroupId: flexGroupIdParam } = useParams()
  const flexGroupId = parseInt(flexGroupIdParam ?? '')
  const { data, isLoading } = useGetById(flexGroupId)
  const { mutateAsync: addFlexGroupMemberAsync } = useAddFlexGroupMember()
  const { mutateAsync: removeFlexGroupMemberAsync } = useRemoveFlexGroupMember()
  const { mutateAsync: addFlexGroupUserAsync } = useAddFlexGroupUser()
  const { mutateAsync: removeFlexGroupUserAsync } = useRemoveFlexGroupUser()
  const toast = useToast()

  const [flexGroup, setFlexGroup] = useState<FlexGroup | undefined>()

  useEffect(() => {
    setFlexGroup(data?.data)
  }, [data])

  const addMember = (item: OrganizationUnitGridItem) =>
    addFlexGroupMemberAsync({ flexGroupId, memberOuId: item.id })
      .then(() => {
        setFlexGroup({
          ...flexGroup!,
          organizationUnits: [...flexGroup!.organizationUnits!, item],
        })
        toast({
          title: 'Organizace byla úspěšně přidána do flexibilní skupiny',
          status: 'success',
        })
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při přidávání organizace do flexibilní skupiny',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })
      })

  const removeMember = (item: OrganizationUnitGridItem) =>
    removeFlexGroupMemberAsync({ flexGroupId, memberOuId: item.id })
      .then(() => {
        setFlexGroup({
          ...flexGroup!,
          organizationUnits: flexGroup!.organizationUnits!.filter(
            (x) => x.id !== item.id,
          ),
        })
        toast({
          title: 'Organizace byla úspěšně odebrána z flexibilní skupiny',
          status: 'success',
        })
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při odebírání organizace z flexibilní skupiny',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })
      })

  const addUser = (item: UserGridItem) =>
    addFlexGroupUserAsync({ flexGroupId, userId: item.userId })
      .then(() => {
        setFlexGroup({ ...flexGroup!, users: [...flexGroup!.users!, item] })
        toast({
          title: 'Uživatel byl úspěšně přidán do flexibilní skupiny',
          status: 'success',
        })
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při přidávání uživatele do flexibilní skupiny',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })
      })

  const removeUser = (item: UserGridItem) =>
    removeFlexGroupUserAsync({ flexGroupId, userId: item.userId })
      .then(() => {
        setFlexGroup({
          ...flexGroup!,
          users: flexGroup!.users!.filter((x) => x.userId !== item.userId),
        })
        toast({
          title: 'Uživatel byl úspěšně odebrán z flexibilní skupiny',
          status: 'success',
        })
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při odebírání uživatele z flexibilní skupiny',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })
      })

  return (
    <Section>
      <Container>
        <PageContent>
          <PageToolbar
            leftElements={
              <Flex sx={{ gap: 4, alignItems: 'center' }}>
                <Heading as="h1">{flexGroup?.name}</Heading>
                <Badge colorScheme="blue">Flexibilní skupina</Badge>
                <ValidityBadge valid={flexGroup?.valid ?? false} />
              </Flex>
            }
            rightElements={
              <Flex sx={{ gap: 4 }}>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/flex-group/list')}
                >
                  <ArrowLeft color="secondary.500" />
                  Zpět na přehled flexibilních skupin
                </Button>
                <Button colorScheme="primary" onClick={() => navigate('edit')}>
                  <Edit />
                  Upravit flexibilní skupinu
                </Button>
              </Flex>
            }
          />

          {flexGroup?.description && (
            <Text size={'md'} maxWidth={'xl'}>
              <TextUnorderedList boxSize={'16px'} mr={4} mt={-0.5} />
              {flexGroup?.description}
            </Text>
          )}

          {!flexGroup ? (
            <Loading large />
          ) : (
            <Flex direction={'column'} gap={8}>
              <Flex direction={'column'} gap={4}>
                <Flex
                  justifyContent={'space-between'}
                  alignItems={'start'}
                  flexDir={{ base: 'column', sm: 'row' }}
                >
                  <SectionHeading>
                    Organizace ve flexibilní skupině
                  </SectionHeading>
                  <AddFlexGroupMember
                    onAddMember={addMember}
                    alreadyAdded={flexGroup.organizationUnits}
                  />
                </Flex>
                <organizationListContext.Provider
                  value={{
                    loading: isLoading,
                    totalCount: flexGroup.organizationUnits?.length ?? 0,
                    currentPage: 1,
                    pageSize: flexGroup.organizationUnits?.length ?? 0,
                    filter: {
                      showInvalid: false,
                      showRootOnly: true,
                      pageIndex: 1,
                    },
                    setCurrentPage: noop,
                    setPageSize: noop,
                    updateFilter: noop,
                    reload: noop,
                  }}
                >
                  <OrganizationsList
                    organizations={flexGroup.organizationUnits}
                    columnsDefinition={[
                      OrganizationListNameColumn,
                      OrganizationListContactPersonColumn,
                      OrganizationListCityColumn,
                      OrganizationListDescriptionColumn,
                      OrganizationListTypeColumn,
                      {
                        key: 'actions',
                        name: '',
                        cellExpr: (item) => (
                          <ListItemRemovalButton
                            sx={{ ml: 'auto' }}
                            onClick={() => removeMember(item)}
                          />
                        ),
                      },
                    ]}
                  />
                </organizationListContext.Provider>
              </Flex>

              <Flex direction={'column'} gap={4}>
                <Flex
                  justifyContent={'space-between'}
                  alignItems={'start'}
                  flexDir={{ base: 'column', sm: 'row' }}
                >
                  <SectionHeading>
                    Uživatelé ve flexibilní skupině
                  </SectionHeading>
                  <AddFlexGroupUser
                    onAddUser={addUser}
                    alreadyAdded={flexGroup.users}
                  />
                </Flex>
                <userListContext.Provider
                  value={{
                    loading: isLoading,
                    totalCount: flexGroup.organizationUnits?.length ?? 0,
                    currentPage: 1,
                    pageSize: flexGroup.organizationUnits?.length ?? 0,
                    filter: {
                      showInvalid: false,
                      pageIndex: 1,
                    },
                    setCurrentPage: noop,
                    setPageSize: noop,
                    updateFilter: noop,
                    reload: noop,
                  }}
                >
                  <UserList
                    users={flexGroup.users}
                    columnsDefinition={[
                      ...UserListDefaultColumns,
                      {
                        key: 'removeFromFlexGroup',
                        name: '',
                        cellExpr: (item) => (
                          <ListItemRemovalButton
                            sx={{ ml: 'auto' }}
                            onClick={() => removeUser(item)}
                          />
                        ),
                      },
                    ]}
                    sx={{ boxShadow: 'card' }}
                  />
                </userListContext.Provider>
              </Flex>
            </Flex>
          )}
        </PageContent>
      </Container>
    </Section>
  )
}
